import _ from 'lodash'
import * as service from '../services'
import {
  getListToGeneralCondition,
  getListItemsToRemove,
} from './formikUpdate'
import {
  actionFailed,
  actionsHaveFailures,
  createOperationReport,
  formatOperationMessage,
  validateOperationResponse,
} from './operationReport'

const META_ID_BY_KEY = {
  altitud: 1,
  longX: 2,
  latY: 3,
  cap: 4,
  dap: 5,
  ht: 6,
  hc: 7,
  copaNS: 8,
  copaEW: 9,
  follaje: 10,
  madurez: 11,
  floracion: 12,
  fructificacion: 13,
  fusteRectitud: 14,
  espaciamiento: 15,
  bifurcaciones: 16,
  inclArbol: 17,
  compInclArbol: 18,
}

const FEATURE_KEYS = [
  'longX', 'latY', 'altitud',
  'dap', 'cap', 'ht', 'hc', 'copaNS', 'copaEW',
  'bifurcaciones', 'inclArbol', 'compInclArbol',
  'follaje', 'madurez', 'floracion', 'fructificacion',
  'fusteRectitud', 'espaciamiento',
]

const REGISTRATION_STATUS_VALUES = ['no_verificado', 'en_revision', 'verificado']

const normalizeEstadoRegistro = (value, treeVerified) => {
  if (REGISTRATION_STATUS_VALUES.includes(value)) return value
  return treeVerified ? 'verificado' : 'no_verificado'
}

const cleanRevisionObservations = value => {
  if (!Array.isArray(value)) return []
  return value
    .map(item => ({
      ...item,
      texto: String(item?.texto ?? '').trim(),
    }))
    .filter(item => item.texto)
}

const normalizeObservationString = value =>
  JSON.stringify(cleanRevisionObservations(value))

/**
 * Actualiza un árbol existente desde el wizard V2.
 *
 * Flujo:
 * 1. Diff tabla árbol: especificaciones generales que cambiaron
 * 2. Diff features: coordenadas + dasométricos + fitosanitarios
 * 3. Inconvenencias: agregar/actualizar las que cambiaron
 * 4. Inconvenencias: eliminar las que se quitaron
 * 5. Fotos: eliminar las marcadas para borrar
 * 6. Fotos: subir las nuevas
 *
 * @param {Object} params
 * @param {Object} params.values          - Formik values finales (combinedValues)
 * @param {Object} params.treeData        - Datos originales del árbol (getPropertiesByID)
 * @param {Array}  params.temporal        - Estado Redux treeFeatures.temporal (inconveniences actuales)
 * @param {Array}  params.stateInitial    - ITEMS_INCONVENIENCE_FORMIK de setItemsFormik (estado inicial)
 * @param {Object} params.groupsFields    - enableSaveButton de Redux treeFeatures (autocompletes)
 * @param {Array}  params.photos          - Array de fotos (existentes + nuevas)
 * @param {Array}  params.deletedPhotoIds - IDs de fotos existentes a eliminar
 * @param {string} params.token           - Bearer token
 * @returns {{ stored: boolean, message?: string }}
 */
export async function submitTreeUpdateV2({
  values,
  treeData,
  temporal,
  stateInitial,
  groupsFields,
  photos = [],
  deletedPhotoIds = [],
  token,
  selectedTenantId = null,
  initialValues = null,
}) {
  const treeId = treeData?.id
  if (!treeId) return { stored: false, message: 'ID de árbol no encontrado.' }
  let targetCantonSlug = null
  const report = createOperationReport()

  const fail = (message, reportPatch = {}) => ({
    stored: false,
    message,
    treeId,
    targetCantonSlug,
    operationReport: {
      action: 'updated',
      ok: false,
      treeId,
      report: createOperationReport({
        ...report,
        ...reportPatch,
      }),
    },
  })

  // ── 0. Cambio de tenant (si el tenant seleccionado difiere del original) ──
  const originalTenantId = treeData?.tenantId ?? null
  if (selectedTenantId !== null && String(selectedTenantId) !== String(originalTenantId ?? '')) {
    const reassignRes = await service.reassignTree(treeId, { tenantId: selectedTenantId }, token)
    if (!reassignRes?.success) {
      return fail(reassignRes?.message || 'Error al cambiar el tenant del árbol.', {
        tree: { ok: false, message: reassignRes?.message || 'Error al cambiar el tenant del árbol.' },
        cache: { ok: false, message: 'No se pudo confirmar el cache tras reasignar el árbol.' },
      })
    }
    targetCantonSlug = reassignRes?.tree?.cantonSlug || null
    report.tree = { ...report.tree, ok: true, message: 'Tenant actualizado.' }
    report.cache = { ...report.cache, ok: true, message: 'Cache actualizado tras reasignación.' }
  }

  // ── 1. Actualizar tabla árbol (solo los campos que cambiaron) ──
  const treeTableDiff = _buildTreeTableDiff(values, treeData, groupsFields)
  if (Object.keys(treeTableDiff).length > 0) {
    const res = await service.updateTreeTable(treeId, treeTableDiff, token)
    if (res?.update === false || res?.error || res?.action === 'error' || _.includes(res, 0)) {
      return fail(res?.message || 'No se pudo actualizar la información general del árbol.', {
        tree: { ok: false, message: res?.message || 'No se pudo actualizar la información general del árbol.' },
      })
    }
    report.tree = { ...report.tree, ok: true, message: 'Información general actualizada.' }
  }

  // ── 1b. Actualizar sitio si algún campo de ubicación cambió ──
  const siteUpdate = _buildSiteUpdate(values, treeData, groupsFields, initialValues)
  if (siteUpdate) {
    const siteRes = await service.createSiteTable(siteUpdate, token)
    if (!_.includes(['creation', 'existing'], siteRes?.typeAction) || !siteRes?.idSitioSeccion) {
      return fail(siteRes?.message || 'No se pudo crear o resolver el sitio del árbol.', {
        site: { ok: false, message: siteRes?.message || 'No se pudo crear o resolver el sitio del árbol.' },
      })
    }

    const updateSiteRes = await service.updateTreeTable(
        treeId,
        { siteSectionId: siteRes.idSitioSeccion, acronym: siteUpdate.siteAcronym ?? '' },
        token,
      )
    if (
      updateSiteRes?.update === false ||
      updateSiteRes?.error ||
      updateSiteRes?.action === 'error' ||
      _.includes(updateSiteRes, 0)
    ) {
      return fail(updateSiteRes?.message || 'No se pudo asociar el árbol al nuevo sitio.', {
        site: { ok: false, message: updateSiteRes?.message || 'No se pudo asociar el árbol al nuevo sitio.' },
      })
    }
    report.site = { ...report.site, ok: true, message: 'Sitio actualizado.' }
  }

  // ── 2. Actualizar features (coordenadas + dasométricos + fitosanitarios) ──
  const featuresDiff = _buildFeaturesDiff(values, treeData)
  if (featuresDiff && Object.keys(featuresDiff).length > 0) {
    const featRes = await service.createFeaturesByKey(treeId, featuresDiff, token)
    const featValidation = validateOperationResponse(featRes, {
      successActions: ['updated'],
      fallbackMessage: 'Error al guardar características del árbol.',
      criticalGroups: ['features', 'cache'],
    })
    if (!featValidation.ok || !Array.isArray(featRes?.actions) || actionsHaveFailures(featRes.actions)) {
      return fail(formatOperationMessage(featRes, 'Error al guardar características del árbol.'), {
        features: {
          ok: false,
          message: formatOperationMessage(featRes, 'Error al guardar características del árbol.'),
          failed: Array.isArray(featRes?.actions) ? featRes.actions.filter(actionFailed) : [],
        },
      })
    }
    report.features = {
      ...report.features,
      ok: true,
      message: 'Características actualizadas.',
      saved: featRes.actions,
      failed: [],
    }
    if (featRes.operationReport?.report?.cache) report.cache = featRes.operationReport.report.cache
  }

  // ── 3. Inconvenencias: agregar/actualizar ──
  const inconvAdd = getListToGeneralCondition(stateInitial, temporal, values)
  if (inconvAdd && Object.keys(inconvAdd).length > 0) {
    const resAddInconv = await service.createAndUpdateInconvenienceLog(treeId, inconvAdd, token)
    if (!Array.isArray(resAddInconv?.actions) || _.some(resAddInconv.actions, actionFailed))
      return fail('Algún inconveniente falló al guardarse.', {
        inconveniences: {
          ok: false,
          message: 'Algún inconveniente falló al guardarse.',
          failed: Array.isArray(resAddInconv?.actions) ? resAddInconv.actions.filter(actionFailed) : [],
        },
      })
    report.inconveniences = {
      ...report.inconveniences,
      ok: true,
      message: 'Inconvenientes actualizados.',
      saved: resAddInconv.actions,
      failed: [],
    }
  }

  // ── 4. Inconvenencias: eliminar las que se quitaron ──
  const inconvRemove = getListItemsToRemove(stateInitial, temporal, values)
  if (inconvRemove && Object.keys(inconvRemove).length > 0) {
    const resDelInconv = await service.disableInconveniences(treeId, inconvRemove, token)
    if (!Array.isArray(resDelInconv) || _.some(resDelInconv, actionFailed))
      return fail('Algún inconveniente falló al eliminarse.', {
        inconveniences: {
          ok: false,
          message: 'Algún inconveniente falló al eliminarse.',
          failed: Array.isArray(resDelInconv) ? resDelInconv.filter(actionFailed) : [],
        },
      })
  }

  // ── 5. Fotos: eliminar las marcadas ──
  for (const photoId of deletedPhotoIds) {
    const photoRes = await service.deleteTreePhoto(photoId, token)
    if (photoRes?.deleted !== true && photoRes?.message !== 'Foto no encontrada') {
      return fail(photoRes?.message || `Error eliminando foto ${photoId}.`, {
        photos: {
          ok: false,
          message: photoRes?.message || `Error eliminando foto ${photoId}.`,
          failed: [{ photoId, message: photoRes?.message || 'No se pudo eliminar la foto.' }],
        },
      })
    }
  }

  // ── 6. Fotos: subir las nuevas ──
  const newPhotos = photos.filter(p => p.isNew && p.file)
  if (newPhotos.length > 0) {
    const photoRes = await service.uploadTreePhotos(treeId, newPhotos, token)
    if (photoRes?.saved !== true) {
      return fail(photoRes?.message || 'Árbol actualizado, pero las fotos quedaron pendientes de subir.', {
        photos: {
          ok: false,
          message: photoRes?.message || 'Las fotos quedaron pendientes de subir.',
          failed: newPhotos.map(photo => ({
            name: photo.originalName || photo.file?.name || 'foto',
            message: photoRes?.message || 'No se pudo subir la foto.',
          })),
        },
      })
    }
    report.photos = {
      ...report.photos,
      ok: true,
      message: photoRes.message || 'Fotos actualizadas.',
      saved: photoRes.photos || [],
      failed: [],
    }
  }

  return {
    stored: true,
    treeId,
    targetCantonSlug,
    operationReport: {
      action: 'updated',
      ok: true,
      treeId,
      report,
    },
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Construye el payload para actualizar el sitio si algún campo de ubicación cambió.
 * Retorna null si no hay cambios; retorna el objeto para createSiteTable si los hay.
 */
function _buildSiteUpdate(values, treeData, groupsFields, initialValues) {
  const { siteField, siteAcronym, sectionField } = groupsFields || {}
  const { site = {} } = treeData

  const newSiteName = siteField?.selected ?? null
  const newAcronym = siteAcronym?.selected ?? null
  const newSection = sectionField?.selected ?? null
  const newTypeSite = values.typeSite_ || null
  const newSitePlace = values.sitePlace_ || null

  // Comparar con originales de nombre/acrónimo/sección
  const origSiteName = site.name ?? null
  const origAcronym = site.siteAcronym ?? null
  const origSection = site.section ?? null

  // typeSite_ y sitePlace_ son IDs en Formik; los initialValues también tienen IDs
  // (convertidos por mapTreeDataToFormikValues), por lo que la comparación es directa.
  const origTypeSite = initialValues?.typeSite_ ?? null
  const origSitePlace = initialValues?.sitePlace_ ?? null
  const typeSiteChanged = newTypeSite !== origTypeSite
  const sitePlaceChanged = newSitePlace !== origSitePlace

  const siteChanged =
    (newSiteName && newSiteName !== origSiteName) ||
    (newAcronym && newAcronym !== origAcronym) ||
    (newSection && newSection !== origSection) ||
    typeSiteChanged ||
    sitePlaceChanged

  if (!siteChanged) return null

  return {
    siteName: newSiteName,
    typeSite: newTypeSite,
    sitePlace: newSitePlace,
    section: newSection,
    siteAcronym: newAcronym,
    parishId: treeData.site?.parish?.id ?? null,
  }
}

/**
 * Construye el diff para la tabla árbol (tb_arbol).
 * Solo incluye los campos que cambiaron respecto a treeData.
 */
function _buildTreeTableDiff(values, treeData, groupsFields) {
  const { specieField } = groupsFields || {}
  const diff = {}

  // Especie
  const newSpecieId = specieField?.selected?.id
  if (newSpecieId && String(newSpecieId) !== String(treeData.specie?.id)) {
    diff.specieId = parseInt(newSpecieId, 10)
  }

  // Vivo / verificado
  const newAlive = Boolean(parseInt(values.alive_, 10))
  if (newAlive !== treeData.alive) diff.alive = newAlive

  const prevEstadoRegistro = normalizeEstadoRegistro(
    treeData.estadoRegistro,
    treeData.treeVerified,
  )
  const newEstadoRegistro = normalizeEstadoRegistro(
    values.estadoRegistro,
    values.treeVerified_ === 1 || values.treeVerified_ === '1',
  )
  const newObservacionesRevision =
    newEstadoRegistro === 'en_revision'
      ? cleanRevisionObservations(values.observacionesRevision)
      : null
  const prevObservacionesRevision =
    prevEstadoRegistro === 'en_revision'
      ? cleanRevisionObservations(treeData.observacionesRevision)
      : null

  if (newEstadoRegistro !== prevEstadoRegistro) {
    diff.estadoRegistro = newEstadoRegistro
    diff.treeVerified = newEstadoRegistro === 'verificado'
    diff.observacionesRevision = newObservacionesRevision
  } else if (
    newEstadoRegistro === 'en_revision' &&
    normalizeObservationString(newObservacionesRevision) !==
      normalizeObservationString(prevObservacionesRevision)
  ) {
    diff.estadoRegistro = newEstadoRegistro
    diff.treeVerified = false
    diff.observacionesRevision = newObservacionesRevision
  }

  // Colector y comentario
  if ((values.recorded_ ?? '') !== (treeData.recorded ?? '')) diff.recorded = values.recorded_
  if ((values.commit_ ?? '') !== (treeData.commit ?? '')) diff.commit = values.commit_

  // Prioridad de atención (nuevo en Feb 2026)
  const newPrioridad = values.prioridadAtencion || null
  const prevPrioridad = _pickFirstDefined(
    treeData.prioridadAtencion,
    treeData.prioridad_atencion,
    treeData.attentionPriority,
  ) ?? null
  if (newPrioridad !== prevPrioridad) {
    if (_hasOwn(treeData, 'prioridad_atencion'))
      diff.prioridad_atencion = newPrioridad
    else diff.prioridadAtencion = newPrioridad
  }

  // Referencia de ubicación
  const newRef = values.referenciaUbicacion_ || null
  const prevRef = treeData.referencia_ubicacion || treeData.locationReference || null
  if (newRef !== prevRef) {
    if (_hasOwn(treeData, 'referencia_ubicacion'))
      diff.referencia_ubicacion = newRef
    else diff.referenciaUbicacion = newRef
  }

  // Actualización de código (solo cuando el usuario eligió explícitamente cambiar)
  const codeAction = values.codeAction || null
  if (codeAction && codeAction !== 'keep') {
    diff.codeMode = codeAction === 'use_plus_code' ? 'PLUS_CODE' : 'STRUCTURED'
    diff.regenerateCode = true
    if (values.codigoManual) diff.codigoManual = values.codigoManual
  }

  return diff
}

/**
 * Construye el diff de features (coordenadas, dasométricos, fitosanitarios).
 * Compara los valores actuales del formulario con treeData.attributes[].
 *
 * Retorna un objeto compatible con service.createFeaturesByKey:
 * { [key]: { id: metafeatureId, value: newValue, verified: null } }
 */
function _buildFeaturesDiff(values, treeData) {
  const { attributes = [] } = treeData
  const diff = {}

  FEATURE_KEYS.forEach(key => {
    const current = values[key]
    const attrObj = attributes.find(
      a =>
        a.metaFeature?.key === key ||
        Number(a.metaFid ?? a.idMetaCaracteristica) === META_ID_BY_KEY[key],
    )

    const initialValue = attrObj?.value != null ? String(attrObj.value) : null
    const currentStr = current != null && current !== '' ? String(current) : null

    // Solo enviar si el valor cambió y el nuevo valor es válido
    if (currentStr !== null && currentStr !== initialValue) {
      const metaId =
        attrObj?.metaFid ??
        attrObj?.idMetaCaracteristica ??
        attrObj?.metaFeature?.id_mc ??
        META_ID_BY_KEY[key]
      if (!metaId) return
      diff[key] = { id: metaId, value: currentStr, verified: null }
    }
  })

  return diff
}

function _pickFirstDefined(...values) {
  return values.find(value => value !== undefined && value !== null)
}

function _hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key)
}
